import axios from "axios";
import axiosInstance from "utils/axios";
import { fetchAttachmentAsFile } from "utils/fileUtils";
import { uploadAttachments, DocumentUploadFeature } from "services/documentUploadService";

/**
 * Helper function to check if the API response is successful.
 * Handles cases where HTTP status is 200 but the response body contains error details.
 */
const checkSuccess = (response) => {
  if (!response) return false;
  const data = response.data;
  if (data) {
    if (data.status === "Success" || data.status === "success") {
      return true;
    }
    if (data.status === "Error" || data.status === "error" || data.status === "Failed" || data.status === "failed") {
      return false;
    }
    if (data.statusCode && (data.statusCode < 200 || data.statusCode >= 300)) {
      return false;
    }
  }
  return response.status === 200;
};


/**
 * Converts a lead to a job
 * @param {Object} payload 
 * @param {string} payload.companyId
 * @param {string} payload.userId
 * @param {string} payload.leadId
 * @param {number} payload.creationMethod (0: scratch, 1: existing, 2: template)
 * @param {string|null} payload.formId
 * @returns {Promise<Object>}
 */
export const convertLeadToJob = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/convert", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully converted to job",
    };
  } catch (error) {
    console.error("Error converting lead to job:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to convert lead to job",
      error: error.response?.data || error,
    };
  }
};

/**
 * Gets a list of jobs with pagination, sorting, and searching
 * @param {Object} payload
 * @param {string} payload.companyId
 * @param {string} payload.userId
 * @param {string} payload.searchText
 * @param {number} payload.pageNumber
 * @param {number} payload.pageSize
 * @param {string} payload.sortColumn
 * @param {string} payload.sortDirection
 * @returns {Promise<Object>}
 */
export const getJobs = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully fetched jobs",
    };
  } catch (error) {
    console.error("Error fetching jobs:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch jobs",
      error: error.response?.data || error,
    };
  }
};

/**
 * Export jobs table data
 * @param {Object} payload 
 * @param {string} format 
 */
export const exportJobsTable = async (payload, format = "pdf") => {
  try {
    payload.format = format;
    const response = await axiosInstance.post(
      `/api/jobs/export/table`,
      payload,
      { responseType: "blob" }
    );

    const ext = format === "pdf" ? "pdf" : "csv";
    const mimeType = format === "pdf" ? "application/pdf" : "text/csv";
    const fileName = `Jobs_Export_${new Date().toISOString().slice(0, 10)}.${ext}`;

    const url = URL.createObjectURL(
      new Blob([response.data], { type: mimeType })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error("Error exporting jobs table:", error);
    return { success: false, error: error.message || "Export failed" };
  }
};

/**
 * Gets job details by id
 * @param {string} id
 * @returns {Promise<Object>}
 */
export const getJobById = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/jobs/${id}`);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully fetched job details",
    };
  } catch (error) {
    console.error(`Error fetching job details for id ${id}:`, error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch job details",
      error: error.response?.data || error,
    };
  }
};

/**
 * Jobs matching a partial title, across the whole company regardless of the caller's own
 * job-visibility permissions -- used while typing a job title in "Add Job" / "Convert Lead to
 * Job" to warn about a possible duplicate before creating one. Mirrors the contact-autocomplete
 * pattern (services/formBuilderService.js's autocompleteContact).
 * @param {string} searchTerm
 * @returns {Promise<Object>}
 */
export const autocompleteJobTitle = async (searchTerm) => {
  try {
    const response = await axiosInstance.get("/api/jobs/autocomplete-title", {
      params: { searchTerm },
    });
    let data = response.data?.data ?? response.data?.Data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (e) {
        data = [];
      }
    }
    return {
      success: checkSuccess(response),
      data: Array.isArray(data) ? data : [],
    };
  } catch (error) {
    console.error("Error fetching job title suggestions:", error);
    return { success: false, data: [] };
  }
};

/**
 * Gets job details by lead id
 * @param {string} leadId
 * @returns {Promise<Object>}
 */
export const getJobByLeadId = async (leadId) => {
  try {
    const response = await axiosInstance.get(`/api/jobs/by-lead/${leadId}`);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully fetched job details by lead id",
    };
  } catch (error) {
    console.error(`Error fetching job details for lead id ${leadId}:`, error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch job details by lead id",
      error: error.response?.data || error,
    };
  }
};

/**
 * Updates job details
 * @param {Object} payload 
 * @returns {Promise<Object>}
 */
export const updateJobDetails = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/UpdateJobDetails", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully updated job details",
    };
  } catch (error) {
    console.error("Error updating job details:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to update job details",
      error: error.response?.data || error,
    };
  }
};

/**
 * Updates job manual status
 * @param {string} jobId 
 * @param {number} statusId 
 * @returns {Promise<Object>}
 */
export const updateJobManualStatus = async (jobId, statusId) => {
  try {
    const response = await axiosInstance.post("/api/jobs/manual-status", {
      jobId,
      statusId,
    });
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Status updated successfully",
    };
  } catch (error) {
    console.error("Error updating job status:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to update job status",
      error: error.response?.data || error,
    };
  }
};

let cachedJobStatusesPromise = null;

export const clearJobStatusesCache = () => {
  cachedJobStatusesPromise = null;
};

/**
 * Fetches all available job statuses from the database.
 * Returns an array of { StatusId, StatusName } objects.
 * Caches the in-flight/resolved promise so multiple calls share a single network request.
 * @param {boolean} [forceRefresh=false]
 * @returns {Promise<Object>}
 */
export const getJobStatuses = async (forceRefresh = false) => {
  if (!forceRefresh && cachedJobStatusesPromise) {
    return cachedJobStatusesPromise;
  }

  cachedJobStatusesPromise = (async () => {
    try {
      const response = await axiosInstance.get("/api/jobs/statuses");
      return {
        success: checkSuccess(response),
        data: response.data?.data
          ? JSON.parse(response.data.data)
          : [],
        message: response.data?.message || "Statuses fetched successfully",
      };
    } catch (error) {
      cachedJobStatusesPromise = null;
      console.error("Error fetching job statuses:", error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || "Failed to fetch job statuses",
        error: error.response?.data || error,
      };
    }
  })();

  return cachedJobStatusesPromise;
};

/**
 * Upserts (creates or updates) a job status
 * @param {Object} payload 
 * @param {number} payload.statusId
 * @param {string} payload.statusName
 * @param {string} payload.alias
 * @param {string} payload.companyId
 * @param {string} payload.userId
 * @returns {Promise<Object>}
 */
export const upsertJobStatus = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/Jobs/UpsertJobStatus", payload);
    clearJobStatusesCache();
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully saved job status",
    };
  } catch (error) {
    console.error("Error upserting job status:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to save job status",
      error: error.response?.data || error,
    };
  }
};

/**
 * Deletes a job status
 * @param {number} statusId
 * @returns {Promise<Object>}
 */
export const deleteJobStatus = async (statusId) => {
  try {
    const response = await axiosInstance.post(`/api/Jobs/deleteJobStatus?statusId=${statusId}`);
    clearJobStatusesCache();
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully deleted job status",
    };
  } catch (error) {
    console.error("Error deleting job status:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to delete job status",
      error: error.response?.data || error,
    };
  }
};

/**
 * Get distinct dynamic columns defined for Jobs.
 * @returns {Promise<Object>}
 */
export const getJobColumns = async () => {
  try {
    const response = await axiosInstance.get("/api/jobs/columns");
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let columns = [];
      if (typeof responseData.data === "string") {
        try {
          columns = JSON.parse(responseData.data);
        } catch (e) {
          console.error("Error parsing columns data:", e);
        }
      } else if (Array.isArray(responseData.data)) {
        columns = responseData.data;
      }
      return { success: true, data: columns, message: responseData.message };
    }
    throw new Error(responseData.message || "Failed to fetch columns");
  } catch (error) {
    console.error("Error fetching job columns:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch columns",
      data: [],
    };
  }
};

/**
 * Gets prefill data for converting a lead to a job
 * @param {string} leadId
 * @returns {Promise<Object>}
 */
export const getConvertJobPrefill = async (leadId) => {
  try {
    const response = await axiosInstance.get(`/api/jobs/convert-job-prefill/${leadId}`);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully fetched prefill data",
    };
  } catch (error) {
    console.error(`Error fetching prefill data for leadId ${leadId}:`, error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch prefill data",
      error: error.response?.data || error,
    };
  }
};

/**
 * Gets the list of job groups
 * @returns {Promise<Object>}
 */
export const getJobGroups = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/api/jobs/groups", { params });
    let parsedData = response.data?.data;
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (e) {
        console.error("Failed to parse stringified job groups data:", e);
      }
    }
    return {
      success: checkSuccess(response),
      data: Array.isArray(parsedData) ? parsedData : (response.data?.data || response.data || []),
      message: response.data?.message || "Successfully fetched job groups",
    };
  } catch (error) {
    console.error("Error fetching job groups:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch job groups",
      error: error.response?.data || error,
    };
  }
};

/**
 * Creates or updates a job group
 * @param {Object} payload 
 * @param {string|null} payload.groupId - null for creating, existing UUID string for editing/updating
 * @param {string} payload.groupName
 * @param {string} payload.companyId
 * @param {string} payload.userId
 * @returns {Promise<Object>}
 */
export const createJobGroup = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/groups", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully saved job group",
    };
  } catch (error) {
    console.error("Error saving job group:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to save job group",
      error: error.response?.data || error,
    };
  }
};

/**
 * Deletes a job group
 * @param {string} groupId 
 * @returns {Promise<Object>}
 */
export const deleteJobGroup = async (groupId) => {
  try {
    const response = await axiosInstance.post(`/api/jobs/deleteGroups/${groupId}`);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully deleted job group",
    };
  } catch (error) {
    console.error(`Error deleting job group ${groupId}:`, error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to delete job group",
      error: error.response?.data || error,
    };
  }
};

/**
 * Gets master fields from FormDesk (for the Custom Fields step).
 * Returns an array of categories, each with a MasterFields array.
 * @param {boolean|null} isSelectionModule - pass false to exclude Selection-only fields (Jobs is a Lead/Job context)
 * @returns {Promise<Object>}
 */
export const getMasterFields = async (isSelectionModule = null) => {
  try {
    const params = isSelectionModule !== null ? { isSelectionModule } : {};
    const response = await axiosInstance.get("/api/FormDesk/GetMasterFields", { params });
    const responseData = response.data;
    let categoriesData = [];
    if (responseData?.data) {
      if (typeof responseData.data === "string") {
        try {
          categoriesData = JSON.parse(responseData.data);
        } catch (e) {
          console.error("Failed to parse master fields data:", e);
        }
      } else if (Array.isArray(responseData.data)) {
        categoriesData = responseData.data;
      }
    }
    return {
      success: true,
      data: categoriesData,
      message: responseData?.message || "Master fields fetched successfully",
    };
  } catch (error) {
    console.error("Error fetching master fields:", error);
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || "Failed to fetch master fields",
      error: error.response?.data || error,
    };
  }
};

/**
 * Gets used job master fields from FormDesk (for Job Filters).
 * Returns an array of categories, each with a MasterFields array.
 * @returns {Promise<Object>}
 */
export const getUsedJobMasterFields = async () => {
  try {
    const response = await axiosInstance.get("/api/FormDesk/GetUsedJobMasterFields");
    const responseData = response.data;
    let categoriesData = [];
    if (responseData?.data) {
      if (typeof responseData.data === "string") {
        try {
          categoriesData = JSON.parse(responseData.data);
        } catch (e) {
          console.error("Failed to parse used job master fields data:", e);
        }
      } else if (Array.isArray(responseData.data)) {
        categoriesData = responseData.data;
      }
    }
    return {
      success: true,
      data: categoriesData,
      message: responseData?.message || "Used job master fields fetched successfully",
    };
  } catch (error) {
    console.error("Error fetching used job master fields:", error);
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || "Failed to fetch used job master fields",
      error: error.response?.data || error,
    };
  }
};

/**
 * Gets the list of contractors
 * @returns {Promise<Object>}
 */
export const getContractors = async () => {
  try {
    const response = await axiosInstance.get("/api/jobs/getContractors");
    const responseData = response.data;
    let contractorsData = [];
    if (responseData?.data) {
      if (typeof responseData.data === "string") {
        try {
          contractorsData = JSON.parse(responseData.data);
        } catch (e) {
          console.error("Failed to parse contractors data:", e);
        }
      } else if (Array.isArray(responseData.data)) {
        contractorsData = responseData.data;
      }
    } else if (Array.isArray(responseData)) {
      contractorsData = responseData;
    }
    return {
      success: checkSuccess(response),
      data: contractorsData,
      message: responseData?.message || "Successfully fetched contractors",
    };
  } catch (error) {
    console.error("Error fetching contractors:", error);
    return { 
      success: false,
      message: error.response?.data?.message || "Failed to fetch contractors",
      error: error.response?.data || error,
    };
  }
};

/**
 * Adds contractors to a job
 * @param {Object} payload 
 * @returns {Promise<Object>}
 */
export const addContractors = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/Jobs/AddContractors", payload);
    return {
      success: checkSuccess(response),
      data: response.data?.data || response.data,
      message: response.data?.message || "Contractors added successfully",
    };
  } catch (error) {
    console.error("Error adding contractors:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to add contractors",
      error: error.response?.data || error,
    };
  }
};

/**
 * Gets a list of internal users
 * @returns {Promise<Object>}
 */
export const getInternalUsers = async () => {
  try {
    let response;
    try {
      response = await axiosInstance.get("/api/jobs/internal-users");
    } catch (apiErr) {
      response = await axiosInstance.get("/api/Jobs/InternalUsers");
    }
    const responseData = response.data;
    let usersData = [];
    if (responseData?.data) {
      if (typeof responseData.data === "string") {
        try {
          usersData = JSON.parse(responseData.data);
        } catch (e) {
          console.error("Failed to parse internal users data:", e);
        }
      } else if (Array.isArray(responseData.data)) {
        usersData = responseData.data;
      }
    } else if (Array.isArray(responseData)) {
      usersData = responseData;
    }

    const normalizedUsers = (usersData || []).map((u) => ({
      ...u,
      id: u.UserId || u.userId || u.id,
      userId: u.UserId || u.userId || u.id,
      fullName: u.FullName || u.fullName || u.name,
      name: u.FullName || u.fullName || u.name,
      label: u.FullName || u.fullName || u.name,
      value: u.UserId || u.userId || u.id,
      designationName: u.DesignationName || u.designationName,
    }));

    return {
      success: checkSuccess(response),
      data: normalizedUsers,
      message: responseData?.message || "Successfully fetched internal users",
    };
  } catch (error) {
    console.error("Error fetching internal users:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch internal users",
      error: error.response?.data || error,
    };
  }
};

/**
 * Gets a list of project managers
 * @returns {Promise<Object>}
 */
export const getProjectManagers = async () => {
  try {
    const response = await axiosInstance.get("/api/Jobs/ProjectManagers");
    const responseData = response.data;
    let pmData = [];
    if (responseData?.data) {
      if (typeof responseData.data === "string") {
        try {
          pmData = JSON.parse(responseData.data);
        } catch (e) {
          console.error("Failed to parse project managers data:", e);
        }
      } else if (Array.isArray(responseData.data)) {
        pmData = responseData.data;
      }
    } else if (Array.isArray(responseData)) {
      pmData = responseData;
    }
    return {
      success: checkSuccess(response),
      data: pmData,
      message: responseData?.message || "Successfully fetched project managers",
    };
  } catch (error) {
    console.error("Error fetching project managers:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch project managers",
      error: error.response?.data || error,
    };
  }
};

/**
 * Assign internal users to a job
 * @param {Object} payload
 * @param {string} payload.jobId
 * @param {Array<{UserId: string, DesignationIds?: string[]}>} payload.internalUsers - DesignationIds
 *   is the full set of designations that user acts as on THIS job (a person may hold more than
 *   one). Omit the key to leave that user's designations untouched; pass [] to clear them.
 * @param {string} payload.companyId
 * @param {string} payload.doneBy
 * @returns {Promise<Object>}
 */
export const addInternalUser = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/Jobs/AddInternalUser", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully added internal users",
    };
  } catch (error) {
    console.error("Error adding internal users:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to add internal users",
      error: error.response?.data || error,
    };
  }
};

/**
 * Checks if a user is assigned to a job
 * @param {Object} payload
 * @param {string} payload.jobId
 * @param {string|null} payload.externalUserId
 * @param {string|null} payload.internalUserId
 * @returns {Promise<Object>}
 */
export const checkUserAssignment = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/Jobs/check-user-assignment", payload);
    return {
      success: checkSuccess(response),
      data: response.data?.data || response.data,
      message: response.data?.message || "Successfully checked user assignment",
    };
  } catch (error) {
    console.error("Error checking user assignment:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to check user assignment",
      error: error.response?.data || error,
    };
  }
};

/**
 * Uploads job documents via presigned S3 URLs (Initiate -> PUT to S3 -> Confirm).
 * @param {string} jobId
 * @param {File[]} files
 * @param {string} [folderId] - optional
 * @returns {Promise<Object>}
 */
export const uploadJobDocuments = async (jobId, files, folderId, taskInfo = null) => {
  try {
    const fileMetas = files.map((file) => ({
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      fileSizeBytes: file.size,
    }));

    const initiateResponse = await axiosInstance.post(
      "/api/Jobs/InitiateJobDocumentUpload",
      { jobId, folderId, files: fileMetas },
    );
    const presignedItems = initiateResponse.data?.data || [];

    if (presignedItems.length !== files.length) {
      throw new Error("Upload initialization failed: mismatched file count.");
    }

    // Initiate resolves FolderId itself when it wasn't passed in (default folder / General /
    // freshly provisioned) and returns the resolved id on each presigned item. Confirm must reuse
    // that exact id - re-resolving independently here could land on a different folder if the
    // job's default folder changed in between (or on first upload, provision a second General).
    const resolvedFolderId = presignedItems[0]?.folderId ?? folderId;

    await Promise.all(
      files.map((file, index) =>
        axios.put(presignedItems[index].uploadUrl, file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        }),
      ),
    );

    const confirmFiles = files.map((file, index) => {
      const dotIndex = file.name.lastIndexOf(".");
      return {
        fileKey: presignedItems[index].fileKey,
        fileName: file.name,
        fileExtension: dotIndex >= 0 ? file.name.slice(dotIndex) : "",
        contentType: file.type || "application/octet-stream",
      };
    });

    const confirmPayload = {
      jobId,
      folderId: resolvedFolderId,
      files: confirmFiles,
    };

    if (taskInfo) {
      if (typeof taskInfo === "string") {
        confirmPayload.taskName = taskInfo;
      } else {
        if (taskInfo.taskId) confirmPayload.jobTaskId = taskInfo.taskId;
        if (taskInfo.taskName) confirmPayload.taskName = taskInfo.taskName;
      }
    }

    const confirmResponse = await axiosInstance.post(
      "/api/Jobs/ConfirmJobDocumentUpload",
      confirmPayload,
    );
    const results = confirmResponse.data?.data || [];
    const allSucceeded = results.length > 0 && results.every((r) => r.success);

    if (allSucceeded) {
      return {
        success: true,
        data: results,
        message: confirmResponse.data?.message || "Job documents uploaded successfully",
      };
    }

    // Normalize per-file confirm failures into the {Errors, FileResults} shape the modal already parses
    const fileResults = results
      .filter((r) => !r.success)
      .map((r) => ({ FileName: r.fileName, Errors: [r.message || "Upload failed"] }));

    return {
      success: false,
      message: confirmResponse.data?.message || "Upload failed",
      status: 400,
      statusCode: 400,
      data: { data: JSON.stringify({ Errors: fileResults.map((f) => f.Errors[0]), FileResults: fileResults }) },
    };
  } catch (error) {
    console.error("Error uploading job documents:", error);

    // Requests rejected by the axios interceptor carry {message, status, statusCode, data} directly
    // (e.g. Initiate's file-validation failures) rather than the raw AxiosError shape.
    if (error.status && error.data) {
      return {
        success: false,
        message: error.message || "Upload failed",
        status: error.status,
        data: error.data,
        statusCode: error.statusCode,
      };
    }

    return {
      success: false,
      message: error.message || "Failed to upload job documents",
      data: null,
    };
  }
};

/**
 * GET /api/Jobs/DownloadJobDocument and trigger a direct download of the document
 * @param {string} documentId
 * @param {string} defaultFileName
 * @returns {Promise<Object>}
 */
export const downloadJobDocument = async (
  documentId,
  defaultFileName = "document",
) => {
  try {
    const url = `/api/Jobs/DownloadJobDocument?documentId=${documentId}`;
    const response = await axiosInstance.get(url, {
      responseType: "blob", // ensure binary stream for documents
    });
    // Determine filename from Content-Disposition header if provided
    const contentDisposition = response.headers?.["content-disposition"];
    let fileName = defaultFileName;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) fileName = match[1];
    }

    // Create blob and programmatically download
    const blob = new Blob([response.data]); // build Blob from binary response
    const href = window.URL.createObjectURL(blob); // object URL for blob
    const a = document.createElement("a");
    a.href = href;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(href); // cleanup

    return { success: true, message: "Document download started" };
  } catch (error) {
    console.error("Error downloading job document:", error);
    return {
      success: false,
      error: error.message || "Failed to download job document",
    };
  }
};

// Get a short-lived presigned S3 URL for previewing a job document (image/PDF/etc.) in a new tab
// or embedded viewer. Documents are stored in S3, not served statically by our API, so callers must
// use this instead of building a URL from the document's raw FilePath/FileKey.
export const getJobDocumentPreviewUrl = async (documentId) => {
  try {
    const response = await axiosInstance.get(
      "/api/Jobs/GetJobDocumentPreviewUrl",
      { params: { documentId } },
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return { success: true, data: responseData.data };
    }
    throw new Error(responseData.message || "Failed to get document preview URL");
  } catch (error) {
    console.error("Error getting job document preview URL:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to get document preview URL",
    };
  }
};

/**
 * Fetch all job options (for dropdowns)
 * @returns {Promise} Response with job options
 */
export const getJobOptions = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/api/Jobs/GetJobOptions", { params });
    const responseData = response.data;
    let raw = responseData?.data ?? responseData ?? [];
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch (_) {
        raw = [];
      }
    }
    return {
      success: true,
      data: Array.isArray(raw) ? raw : [],
      status: responseData?.status || "Success"
    };
  } catch (error) {
    console.error("Error fetching job options:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch job options",
      data: []
    };
  }
};

export const getJobUsers = async (jobId, companyId = null) => {
  try {
    const params = companyId ? { companyId } : {};
    const response = await axiosInstance.get(`/api/jobs/${jobId}/users`, { params });
    const responseData = response.data;
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let raw = responseData.data;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch (_) {
          raw = [];
        }
      }
      return {
        success: true,
        data: Array.isArray(raw) ? raw : [],
        message: responseData.message || "Successfully fetched job users",
      };
    }
    return {
      success: false,
      message: responseData.message || "Failed to fetch job users",
      data: []
    };
  } catch (error) {
    console.error("Error fetching job users:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch job users",
      error: error.response?.data || error,
      data: []
    };
  }
};

export const getJobContractors = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/contractors", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully fetched contractors",
    };
  } catch (error) {
    console.error("Error fetching job contractors:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch contractors",
      error: error.response?.data || error,
    };
  }
};

export const getJobInternalUsers = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/internal-users", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully fetched internal users",
    };
  } catch (error) {
    console.error("Error fetching job internal users:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch internal users",
      error: error.response?.data || error,
    };
  }
};

export const getJobClients = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/clients", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully fetched clients",
    };
  } catch (error) {
    console.error("Error fetching job clients:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch clients",
      error: error.response?.data || error,
    };
  }
};

export const removeJobContractor = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/contractors/remove", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully removed contractor",
    };
  } catch (error) {
    console.error("Error removing job contractor:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to remove contractor",
      error: error.response?.data || error,
    };
  }
};

export const removeJobInternalUser = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/internal-users/remove", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully removed internal user",
    };
  } catch (error) {
    console.error("Error removing job internal user:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to remove internal user",
      error: error.response?.data || error,
    };
  }
};

export const removeJobClient = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/clients/remove", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully removed client",
    };
  } catch (error) {
    console.error("Error removing job client:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to remove client",
      error: error.response?.data || error,
    };
  }
};

export const getUserActiveTasksCount = async (jobId, userId) => {
  try {
    const response = await axiosInstance.get(`/api/jobs/${jobId}/users/${userId}/active-tasks-count`);
    if (response.data?.statusCode === 200 && response.data?.status === "Success") {
      let parsedData = { total: 0, published: 0, draft: 0 };
      const rawData = response.data.data;
      if (typeof rawData === 'string' && rawData.trim().startsWith('{')) {
        try {
          const json = JSON.parse(rawData);
          parsedData = {
            total: Number(json.total) || 0,
            published: Number(json.published) || 0,
            draft: Number(json.draft) || 0,
          };
        } catch (e) {
          const num = Number(rawData) || 0;
          parsedData = { total: num, published: num, draft: 0 };
        }
      } else if (typeof rawData === 'number' || typeof rawData === 'string') {
        const num = Number(rawData) || 0;
        parsedData = { total: num, published: num, draft: 0 };
      } else if (typeof rawData === 'object' && rawData !== null) {
        parsedData = {
          total: Number(rawData.total) || 0,
          published: Number(rawData.published) || 0,
          draft: Number(rawData.draft) || 0,
        };
      }
      return {
        success: true,
        data: parsedData,
        message: response.data.message || "Successfully fetched active task count",
      };
    }
    return {
      success: false,
      data: { total: 0, published: 0, draft: 0 },
      message: response.data?.message || "Failed to fetch active task count",
    };
  } catch (error) {
    console.error("Error fetching user active tasks count:", error);
    return {
      success: false,
      data: { total: 0, published: 0, draft: 0 },
      message: error.response?.data?.message || "Failed to fetch active task count",
      error: error.response?.data || error,
    };
  }
};
export const deleteJobDocument = async (documentId) => {
  try {
    const response = await axiosInstance.post("/api/jobs/DeleteJobDocument", null, {
      params: { documentId },
    });

    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Successfully deleted document",
    };
  } catch (error) {
    console.error("Error deleting job document:", error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to delete document",
      error: error.response?.data || error,
    };
  }
};

export const getJobClientsForEmail = async (jobId) => {
  try {
    const response = await axiosInstance.get(`/api/jobs/${jobId}/email-clients`);
    return {
      success: checkSuccess(response),
      data: response.data?.data ? JSON.parse(response.data.data) : [],
      message: response.data?.message || "Successfully fetched clients for email",
    };
  } catch (error) {
    console.error("Error fetching job clients for email:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch clients for email",
      error: error.response?.data || error,
      data: [],
    };
  }
};

export const sendEmailToJob = async (
  jobId,
  email,
  subject,
  body,
  cc = [],
  bcc = [],
  attachments = [],
  ProviderCode = "",
  templateId = null,
  preferredDeliveryDate = null,
  preferredDeliveryTime = null,
  clientIds = [],
) => {
  try {
    const clientsPayload = (clientIds || []).map(id => ({
      clientId: id,
      email: email
    }));
    const jobsPayload = [
      {
        jobId: jobId,
        clients: clientsPayload
      }
    ];

    // Template attachments are resolved server-side via TemplateId and skipped here.
    const filesToUpload = [];
    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        if (file instanceof File) {
          filesToUpload.push(file);
        } else if (file.templateAttachment && file.path) {
          continue;
        } else if (typeof file === "string") {
          try {
            filesToUpload.push(await fetchAttachmentAsFile(file));
          } catch (fetchError) {
            console.error("Error fetching attachment from URL:", fetchError);
          }
        }
      }
    }

    let uploadedAttachments = [];
    if (filesToUpload.length > 0) {
      const uploadResult = await uploadAttachments(DocumentUploadFeature.EMAIL_ATTACHMENT, filesToUpload);
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload attachments");
      }
      uploadedAttachments = uploadResult.data.map((item, idx) => {
        const bytes = filesToUpload[idx]?.size || (item.fileSizeKB ? item.fileSizeKB * 1024 : 0);
        return {
          fileName: item.fileName,
          filePath: item.filePath,
          fileExtension: item.fileExtension,
          fileBytes: bytes,
          fileSizeBytes: bytes
        };
      });
    }

    const payload = {
      jobs: jobsPayload,
      Subject: subject,
      Body: body,
      ProviderCode,
      CC: cc && cc.length > 0 ? cc.join(",") : undefined,
      Bcc: bcc && bcc.length > 0 ? bcc.join(",") : undefined,
      TemplateId: templateId || undefined,
      Attachments: uploadedAttachments,
    };

    if (preferredDeliveryDate && preferredDeliveryTime) {
      payload.PreferredDeliveryDate = preferredDeliveryDate;
      payload.PreferredDeliveryTime = preferredDeliveryTime;
    } else if (preferredDeliveryDate) {
      payload.PreferredDeliveryDate = preferredDeliveryDate;
    }

    const response = await axiosInstance.post(
      `/api/Email/send-to-job`,
      payload,
      {
        timeout: 60000,
      }
    );

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Email sent successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to send email");
    }
  } catch (error) {
    console.error("Error sending email:", error);
    throw new Error(error.response?.data?.message || error.message || "Failed to send email");
  }
};

export const sendEmailToMultipleJobs = async (
  jobsData,
  subject,
  body,
  cc = [],
  bcc = [],
  attachments = [],
  ProviderCode = "",
  templateId = null,
  preferredDeliveryDate = null,
  preferredDeliveryTime = null,
) => {
  try {
    const jobsMap = {};

    jobsData.forEach(item => {
      const jobId = item.id;
      if (jobId) {
        if (!jobsMap[jobId]) {
          jobsMap[jobId] = [];
        }
        if (item._contactId) {
          jobsMap[jobId].push({
            clientId: item._contactId,
            email: item.email
          });
        }
      }
    });

    const jobsPayload = Object.keys(jobsMap).map(jobId => ({
      jobId: jobId,
      clients: jobsMap[jobId]
    }));

    // Template attachments are resolved server-side via TemplateId and skipped here.
    const filesToUpload = [];
    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        if (file instanceof File) {
          filesToUpload.push(file);
        } else if (file.templateAttachment && file.path) {
          continue;
        } else if (typeof file === "string") {
          try {
            filesToUpload.push(await fetchAttachmentAsFile(file));
          } catch (fetchError) {
            console.error("Error fetching attachment from URL:", fetchError);
          }
        }
      }
    }

    let uploadedAttachments = [];
    if (filesToUpload.length > 0) {
      const uploadResult = await uploadAttachments(DocumentUploadFeature.EMAIL_ATTACHMENT, filesToUpload);
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload attachments");
      }
      uploadedAttachments = uploadResult.data.map((item, idx) => {
        const bytes = filesToUpload[idx]?.size || (item.fileSizeKB ? item.fileSizeKB * 1024 : 0);
        return {
          fileName: item.fileName,
          filePath: item.filePath,
          fileExtension: item.fileExtension,
          fileBytes: bytes,
          fileSizeBytes: bytes
        };
      });
    }

    const payload = {
      jobs: jobsPayload,
      Subject: subject,
      Body: body,
      ProviderCode,
      CC: cc && cc.length > 0 ? cc.join(",") : undefined,
      Bcc: bcc && bcc.length > 0 ? bcc.join(",") : undefined,
      TemplateId: templateId || undefined,
      Attachments: uploadedAttachments,
    };

    if (preferredDeliveryDate && preferredDeliveryTime) {
      payload.PreferredDeliveryDate = preferredDeliveryDate;
      payload.PreferredDeliveryTime = preferredDeliveryTime;
    } else if (preferredDeliveryDate) {
      payload.PreferredDeliveryDate = preferredDeliveryDate;
    }

    const response = await axiosInstance.post(
      `/api/Email/send-to-job`,
      payload,
      {
        timeout: 60000,
      }
    );

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Email sent successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to send email");
    }
  } catch (error) {
    console.error("Error sending email:", error);
    throw new Error(error.response?.data?.message || error.message || "Failed to send email");
  }
};

/**
 * Perform bulk action on jobs (Delete, Change Status)
 * @param {Object} payload
 * @param {number} payload.actionType - 1 = Delete, 2 = Change Status
 * @param {string[]} [payload.jobIds] - List of job UUIDs when explicit selection
 * @param {boolean} [payload.includeAllFiltered] - True to apply to all jobs matching active filters
 * @param {number} [payload.targetStatusId] - Target status ID when actionType = 2
 * @returns {Promise<Object>}
 */
export const bulkJobAction = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/jobs/BulkJobAction", payload);
    return {
      success: checkSuccess(response),
      data: response.data,
      message: response.data?.message || "Bulk job action executed successfully",
    };
  } catch (error) {
    console.error("Error performing bulk job action:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to perform bulk job action",
      error: error.response?.data || error,
    };
  }
};

/**
 * Send email to job users (internal users and/or contractors)
 */
export const sendEmailToJobUsers = async (
  jobId,
  recipients = [],
  subject,
  body,
  cc = [],
  bcc = [],
  attachments = [],
  ProviderCode = "",
  templateId = null,
  preferredDeliveryDate = null,
  preferredDeliveryTime = null,
) => {
  try {
    const filesToUpload = [];
    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        if (file instanceof File) {
          filesToUpload.push(file);
        } else if (file.templateAttachment && file.path) {
          continue;
        } else if (typeof file === "string") {
          try {
            filesToUpload.push(await fetchAttachmentAsFile(file));
          } catch (fetchError) {
            console.error("Error fetching attachment from URL:", fetchError);
          }
        }
      }
    }

    let uploadedAttachments = [];
    if (filesToUpload.length > 0) {
      const uploadResult = await uploadAttachments(DocumentUploadFeature.EMAIL_ATTACHMENT, filesToUpload);
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload attachments");
      }
      uploadedAttachments = uploadResult.data.map((item, idx) => {
        const bytes = filesToUpload[idx]?.size || (item.fileSizeKB ? item.fileSizeKB * 1024 : 0);
        return {
          fileName: item.fileName,
          filePath: item.filePath,
          fileExtension: item.fileExtension,
          fileBytes: bytes,
          fileSizeBytes: bytes
        };
      });
    }

    const payload = {
      jobId: jobId || null,
      recipients: (recipients || []).map(r => ({
        userId: r.userId || r.UserId || null,
        email: r.email || r.Email || ""
      })),
      subject,
      body,
      cc: Array.isArray(cc) ? cc.join(",") : (cc || ""),
      bcc: Array.isArray(bcc) ? bcc.join(",") : (bcc || ""),
      attachments: uploadedAttachments,
      providerCode: ProviderCode || "",
      templateId: templateId || null,
      preferredDeliveryDate: preferredDeliveryDate || null,
      preferredDeliveryTime: preferredDeliveryTime || null,
    };

    const response = await axiosInstance.post("/api/Email/send-to-job-users", payload);

    if (response.data?.statusCode === 200) {
      return {
        success: true,
        message: response.data.message || "Email sent successfully",
        data: response.data.data,
      };
    } else {
      return {
        success: false,
        message: response.data?.message || "Failed to send email",
        error: response.data,
      };
    }
  } catch (error) {
    console.error("Error sending email to job users:", error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to send email",
      error: error.response?.data || error,
    };
  }
};

/**
 * Fetches roles with users list
 * @param {Object} [params] Optional query parameters
 * @returns {Promise<Object>}
 */
export const getRolesWithUsers = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/api/Jobs/roles-with-users", { params });
    let parsedData = response.data?.data;
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (e) {
        console.error("Failed to parse stringified roles with users data:", e);
      }
    }
    return {
      success: checkSuccess(response),
      data: Array.isArray(parsedData) ? parsedData : (response.data?.data || response.data || []),
      message: response.data?.message || "Successfully fetched roles with users",
    };
  } catch (error) {
    console.error("Error fetching roles with users:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch roles with users",
      error: error.response?.data || error,
    };
  }
};

/**
 * Fetches job states list
 * @param {Object} [params] Optional query parameters
 * @returns {Promise<Object>}
 */
export const getJobStates = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/api/Jobs/states", { params });
    let parsedData = response.data?.data;
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (e) {
        console.error("Failed to parse stringified job states data:", e);
      }
    }
    return {
      success: checkSuccess(response),
      data: Array.isArray(parsedData) ? parsedData : (response.data?.data || response.data || []),
      message: response.data?.message || "Successfully fetched job states",
    };
  } catch (error) {
    console.error("Error fetching job states:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch job states",
      error: error.response?.data || error,
    };
  }
};





